import type { ExamTypeListValueEditorViewModel } from '@/shared/api/generated/types';

import type { QuestionAlternative } from '../hooks/useQuestionEdit';

/**
 * Normaliza um texto que pode conter HTML para comparação: remove tags,
 * colapsa espaços em branco e apara as bordas.
 *
 * @param value - Texto possivelmente com HTML
 * @returns Texto plano normalizado
 */
export function normalizeHtml(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Indica se dois campos de texto (possivelmente com HTML) diferem após normalização.
 *
 * @param before - Valor atual
 * @param after - Valor sugerido
 * @returns `true` quando os textos normalizados são diferentes
 */
export function isFieldChanged(
  before: string | null | undefined,
  after: string | null | undefined,
): boolean {
  return normalizeHtml(before) !== normalizeHtml(after);
}

/**
 * Indica se a lista de alternativas foi alterada pela sugestão da IA,
 * comparando quantidade, texto (normalizado) e marcação de resposta correta.
 *
 * @param current - Alternativas atuais do formulário
 * @param suggested - Alternativas sugeridas pela IA
 * @returns `true` quando há qualquer diferença
 */
export function areAlternativesChanged(
  current: readonly QuestionAlternative[],
  suggested: readonly ExamTypeListValueEditorViewModel[] | null | undefined,
): boolean {
  const suggestedList = suggested ?? [];
  if (current.length !== suggestedList.length) return true;

  return current.some((alt, index) => {
    const other = suggestedList[index];
    return (
      isFieldChanged(alt.value, other?.value) || alt.isCorrect !== (other?.correctAnswer ?? false)
    );
  });
}
